import {ensureConnection} from "../../../websocket/websocketUtils.ts";
import {trimMessages} from "../../contextManagement/messageFormatter/Utils.ts";
import modelProviderMapping from "../../../IdMappings/modelProviderMapping.ts";
import {formatMessages} from "../../contextManagement/messageFormatter/formatMessages.ts";
import {getIdToken} from "../../../misc/miscUtils.ts";
import {initializeSystemMessage} from "../../contextManagement/systemMessage/systemMessageUtils.ts";
import {getAutomaticModel} from "../../modelRouter/getAutomaticModel.ts";
import {getModelReasoningEffort} from "../../modelRouter/reasoningEffortRouter.ts";
import {v4 as uuidv4} from "uuid";
import {handleFinishStream} from "../receiver/responseFinish/finishResponse.ts";
import {canSeeImage, canSeeVideo, canHearAudio, isModelPremium} from "../../../misc/modelInsightUtils.ts";
import {findSummaryForInjection} from "../../contextManagement/summaryUtils.ts";

// Helper function to check if a preferred model can handle the conversation requirements
const canUsePreferredModel = (preferredModel: string, contextDetails: any, hasUsageBasedEnabled: boolean, subscriptionPlan: string | null) => {
    // Check if model requires premium access but user doesn't have usage-based pricing
    if (isModelPremium(preferredModel) && !(subscriptionPlan !== null && hasUsageBasedEnabled)) {
        return false;
    }

    // Check if model can handle images when conversation has images
    if (contextDetails.numOfImages > 0 && !canSeeImage(preferredModel)) {
        return false;
    }

    // Check if model can handle video when conversation has video
    if (contextDetails.convoHasVideo && !canSeeVideo(preferredModel)) {
        return false;
    }

    // Check if model can handle audio when conversation has audio
    return !(contextDetails.convoHasAudio && !canHearAudio(preferredModel));
};

export const sendLlmRequest = async (websocketRef: any, settingsObjRef: any, environmentObjRef: any, userObjRef: any, dynamicStateObjRef: any, messagesRef: any, messagesSequenceRef: any, currentMessageRef: any, llmRequestRefs: any, setupWebsocketHelper: any, setMessages: any, updateElementInProgress: any, setDynamicStateObj: any) => {
    await ensureConnection(websocketRef, setupWebsocketHelper);

    llmRequestRefs.current.streamId = uuidv4();
    let { environment, systemDetails, date, projectEmbeddingsLastUpdated, uiVersionTimestamp, userApplicationVersion, latestApplicationVersion, anthropicApiKey, openAiApiKey, xAiApiKey, geminiApiKey, projectName } = environmentObjRef.current;
    let { mode, model, enableSearchInternet, enableGenerateImages, enableCreateDocument, enableRunTerminalCommand, enableReviewChanges, customInstructions, projectCustomInstructions, customModes, claude_sonnet_reasoning_effort, claude_opus_reasoning_effort, apiKeyOnly } = settingsObjRef.current;
    const { firstName, location, subscriptionPlan, hasUsageBasedEnabled, monthlyFreeMessages } = userObjRef.current;
    const { importantMessageIds } = dynamicStateObjRef.current;
    const { topLevelFiles, streamId, aiTurnCounter, lastToolUsed, additionalContext } = llmRequestRefs.current;

    if (apiKeyOnly && !anthropicApiKey && !openAiApiKey && !xAiApiKey && !geminiApiKey) {
        setDynamicStateObj({errorMessage: "API key mode is enabled, but no API keys have been entered. Please go to Settings > General > API Keys > Configure."});
        handleFinishStream(llmRequestRefs, setMessages, updateElementInProgress);
        return;
    }
    // Get project-specific custom instructions for the current project (if available)
    const currentProjectCustomInstructions = (projectName && projectCustomInstructions && projectCustomInstructions[projectName]) || null;

    const canUsePremium = (subscriptionPlan !== null && hasUsageBasedEnabled) || monthlyFreeMessages > 0;
    const enableCodebaseSearch = projectEmbeddingsLastUpdated !== null;
    const enableDeepResearch = false;
    const systemMessage = initializeSystemMessage(environment, model, mode, systemDetails, date, canUsePremium, enableSearchInternet, enableDeepResearch, enableGenerateImages, enableRunTerminalCommand, enableReviewChanges, enableCodebaseSearch, customInstructions, currentProjectCustomInstructions, firstName, location, topLevelFiles, customModes);
    const {trimmedMessages, contextDetails} = trimMessages(messagesRef.current, messagesSequenceRef.current, 5, importantMessageIds, systemMessage);

    // Find summary of truncated turns to inject into context
    const conversationSummary = findSummaryForInjection(trimmedMessages);

    if (model === "Automatic") {
        // Check for custom mode preferred model
        const preferredModel = customModes?.[mode]?.preferredModel;

        if (preferredModel && modelProviderMapping[preferredModel] &&
            canUsePreferredModel(preferredModel, contextDetails, hasUsageBasedEnabled, subscriptionPlan)) {
            model = preferredModel;
        } else {
            // Fall back to automatic model selection
            model = getAutomaticModel(
                subscriptionPlan, canUsePremium, mode, environment,
                contextDetails.numOfImages, contextDetails.convoHasAudio,
                contextDetails.convoHasVideo, contextDetails.approxTokens,
                apiKeyOnly, anthropicApiKey, openAiApiKey, xAiApiKey, geminiApiKey
            );
        }

        currentMessageRef.current.message.answeredBy = model;
    }

    const provider = modelProviderMapping[model];
    let apiKey;
    if (provider === "Anthropic") apiKey = anthropicApiKey;
    else if (provider === "OpenAI") apiKey = openAiApiKey;
    else if (provider === "xAI") apiKey = xAiApiKey;
    else if (provider === "Google") apiKey = geminiApiKey;

    const formattedMessages = await formatMessages(trimmedMessages, environment, systemMessage, provider, model, mode, false, conversationSummary);

    let isAuthenticated = false;
    let token = userObjRef.current.fallbackUserId;
    if (userObjRef.current.user) {
        token = await getIdToken(userObjRef.current.user);
        isAuthenticated = true;
    }

    console.log(formattedMessages)
    websocketRef.current.send(JSON.stringify({
        functionName: "llmRequest",
        args: {
            mode: mode,
            streamId: streamId,
            authToken: token,
            uiVersionTimestamp: uiVersionTimestamp,
            approxTokens: contextDetails.approxTokens,
            model: model,
            provider: provider?.toLowerCase(),
            messages: formattedMessages,
            environment: environment,
            enableSearchInternet: enableSearchInternet,
            enableDeepResearch: enableDeepResearch,
            enableGenerateImages: enableGenerateImages,
            enableCreateDocument: enableCreateDocument,
            enableRunTerminalCommand: enableRunTerminalCommand,
            enableReviewChanges: enableReviewChanges,
            enableCodebaseSearch: enableCodebaseSearch,
            reasoning_effort: getModelReasoningEffort(model, claude_sonnet_reasoning_effort, claude_opus_reasoning_effort, contextDetails, environment),
            isAuthenticated: isAuthenticated,
            subscriptionPlan: subscriptionPlan,
            monthlyFreeMessages: monthlyFreeMessages,
            aiTurnCounter: aiTurnCounter,
            lastToolUsed: lastToolUsed,
            mostRecentFileStates: additionalContext.mostRecentFileStates,
            userApplicationVersion: userApplicationVersion,
            latestApplicationVersion: latestApplicationVersion,
            apiKey: apiKeyOnly ? apiKey : null,
            needsBase64Pulled: model.includes("Gemini")
        }
    }));
};
